import React, { useState, useEffect } from 'react'
import { useTheme, alpha } from '@mui/material'
import { styled } from '@mui/material/styles'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid2'
import Stack from '@mui/material/Stack'
import Button from '@mui/material/Button'
import Tooltip from '@mui/material/Tooltip'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import AddCircleIcon from '@mui/icons-material/AddCircle'
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle'
import DeleteIcon from '@mui/icons-material/Delete'
import { IoMdCheckmarkCircleOutline } from 'react-icons/io'
import { TiWarningOutline } from 'react-icons/ti'
import { TbCertificate, TbCertificateOff } from 'react-icons/tb'
import { uiDomainInitial, uiCertificateInitial } from '../_common/userInterfaceConstants'
import { capitalizeFirstLetter } from '../../_common/_helpers/string'
import {
  checkCertificateValidity,
  checkCertificateMatch,
  checkFullChain,
  getCertificateExpireDate,
  getDomainFromCertificate,
} from '../_helpers/rsaCertificates'
import constants from '../../../config/constants'
import defaultTexts from '../../../texts/texts'
import useWindowDimensions from '../../_common/WindowDimesnions'
import CustomTextField from '../../_common/_form_fields/CustomTextField'
import CustomDialog from '../../_common/CustomDialog'
import CustomColorPicker from '../../_common/CustomColorPicker'

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
})

const AdminUiEdit = (props) => {
  const {
    adminUiDomain,
    setAdminUiDomain,
    adminUiCertificates,
    setAdminUiCertificates,
    adminUiSiteKey,
    setAdminUiSiteKey,
    adminUiSecretKey,
    setAdminUiSecretKey,
    adminUiLogo,
    setAdminUiLogo,
    adminUiPrimaryColor,
    setAdminUiPrimaryColor,
    adminUiSecondaryColor,
    setAdminUiSecondaryColor,
    certificateAction,
    setCertificateAction,
    domainsWithCertififcates,
    verifyDomain,
    errors,
    validateFormFields,
  } = props
  const theme = useTheme()
  const { width } = useWindowDimensions()
  
  const [helperText, setHelperText] = useState('')
  const [helperDialogOpen, setHelperDialogOpen] = useState(false)
  const [primaryColorDialogOpen, setPrimaryColorDialogOpen] = useState(false)
  const [secondaryColorDialogOpen, setSecondaryColorDialogOpen] = useState(false)
  const [anyDomainFilled, setAnyDomainFilled] = useState(false)
  
  const fieldBoxStyle = {
    padding: '12px 0px'
  }

  const helpIconStyle = {
    color: theme.palette.oxfordBlue,
  }

  const formControlStyle = {
    background: theme.palette.aliceBlue,
    color: theme.palette.vodka,
    borderRadius: '5px',
    width: '90%',
  }

  const outlinedInputStyle = {
    '& .MuiInputBase-input::label': {
      color: theme.palette.vodka,
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.vodka,
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.vodka,
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.vodka,
    },
  }

  const sectionLabelStyle = {
    color: theme.palette.oxfordBlue,
    fontSize: '16px',
    fontWeight: '500',
  }

  const appearanceLabelStyle = {
    color: theme.palette.vodka,
    fontSize: '20px',
    fontWeight: '500',
    textAlign: 'center',
    paddingBottom: '20px',
    marginTop: width < constants.lgBreakpoint ? '20px' : '0px',
  }

  const uploadButtonStyle = {
    width: '90px',
    height: '90px',
    borderRadius: '45px',
    color: 'white',
    background: theme.palette.grayX11,
    fontSize: '20px',
    fontWeight: '500',
  }

  const uploadLogoDescriptionStyle = {
    color: theme.palette.greenSheen,
    fontSize: '18px',
    fontWeight: '500',
    width: '30%',
  }

  const pickColorStyle = {
    width: '60px',
    height: '60px',
    borderRadius: '30px',
    cursor: 'pointer',
  }

  const domainPeriodStyle = {
    color: theme.palette.violetsAreBlue,
    fontSize: '32px',
    fontWeight: '500',
    padding: '0px 5px',
  }

  const verifyButtonStyle = {
    fontWeight: '500',
    color: 'white',
    background: theme.palette.greenSheen,
    fontSize: '18px',
    height: '40px',
    borderRadius: '20px',
    padding: '0px 20px',
    textTransform: 'capitalize',

    '&:hover': {
      background: alpha(theme.palette.greenSheen, 0.8),
    },
  }

  const domainVerifyHelperStyle = {
    background: theme.palette.aliceBlue,
    padding: '10px',
    borderRadius: '5px',
    border: '1px solid',
  }

  const rsaSwitchStyle = {
    height: '30px',
    padding: '0px 15px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    color: 'white',
    fontSize: '16px',
    fontWeight: '500',
  }

  const valueMdStyle = {
    color: theme.palette.greenSheen,
    fontSize: '16px',
    fontWeight: '500',
  }

  const getError = (field_key) => {
    const error_object = errors.find(item => item.field_key === field_key)
    return error_object ? true : false
  }

  const validateCertificates = (certificates) => {
    let updated_certs = []

    certificates.forEach(item => {
      let cert_checked = false
      let cert_match = false
      let chain_checked = true
      let expire_date = null
      let domain = null

      if (item.certificate) {
        cert_checked = checkCertificateValidity(item.certificate)
      }

      if (cert_checked && item.private_key) {
        cert_match = checkCertificateMatch(item.certificate, item.private_key)
      }

      if (item.full_chain) {
        chain_checked = checkFullChain(item.full_chain)
      }

      if (cert_checked && cert_match && chain_checked) {
        expire_date = getCertificateExpireDate(item.certificate)
        domain = getDomainFromCertificate(item.certificate)
      }

      updated_certs.push({ ...item, expire_date, domain })
    })

    return updated_certs
  }

  const isDomainWithCertificate = (subdomain_prefix, domain_name) => {
    const domain = subdomain_prefix ? subdomain_prefix + '.' + domain_name : domain_name
    return domainsWithCertififcates.includes(domain)
  }

  const handleHelperDialogOpen = (value) => {
    setHelperDialogOpen(true)
    setHelperText(value)
  }

  const handleHelperDialogClose = () => {
    setHelperDialogOpen(false)
    setHelperText('')
  }

  const handlePrimaryColorDialogOpen = (value) => {
    setPrimaryColorDialogOpen(true)
  }

  const handlePrimaryColorDialogClose = () => {
    setPrimaryColorDialogOpen(false)
  }

  const handleSecondaryColorDialogOpen = (value) => {
    setSecondaryColorDialogOpen(true)
  }

  const handleSecondaryColorDialogClose = () => {
    setSecondaryColorDialogOpen(false)
  }

  const handleAddDomain = () => {
    setAdminUiDomain(prev => [...prev, { ...uiDomainInitial }])
  }

  const handleRemoveDomain = (thisIndex) => {
    setAdminUiDomain(prev => prev.filter((item, index) => index !== thisIndex))
  }

  const handleChangeDomain = (event, field_key, thisIndex) => {
    const value = event.target.value

    setAdminUiDomain(prev => {
      let new_data = []
      prev.forEach((item, index) => {
        if (index === thisIndex) {
          new_data.push({ ...item, [field_key]: value, status: '' })
        } else {
          new_data.push(item)
        }
      })
      return new_data
    })

    validateFormFields()
  }

  const handleAddCertificate = () => {
    setAdminUiCertificates(prev => [...prev, { ...uiCertificateInitial }])
  }

  const handleRemoveCertificate = () => {
    setAdminUiCertificates(prev => prev.filter((item, index) => index !== prev.length - 1))
  }

  const handleChangeCertificates = (event, field_key, thisIndex) => {
    const value = event.target.value
    
    setAdminUiCertificates(prev => {
      let new_data = []
      prev.forEach((item, index) => {
        if (index === thisIndex) {
          new_data.push({ ...item, [field_key]: value })
        } else {
          new_data.push(item)
        }
      })
      new_data = validateCertificates(new_data)
      return new_data
    })

    validateFormFields()    
  }
  
  const handleChangeSiteKey = (e) => {
    const value = e.target.value
    setAdminUiSiteKey(value)
    validateFormFields()
  }

  const handleChangeSecretKey = (e) => {
    const value = e.target.value
    setAdminUiSecretKey(value)
    validateFormFields()
  }

  const handleLogoUpload = (event) => {
    setAdminUiLogo(event.target.files[0])
  }

  const handleChangeCertificateAction = (action) => {
    setCertificateAction(prev => prev === action ? '' : action)
  }

  useEffect(() => {
    const domain_object = adminUiDomain.find(item => item.domain_name !== '')
    setAnyDomainFilled(domain_object ? true : false)
  }, [adminUiDomain])

  const getDomainVerifyHelper = () => {
    return (
      <Box
        sx={{
          ...domainVerifyHelperStyle,
          marginTop: '17px',
          color: anyDomainFilled ? theme.palette.darkCyan : theme.palette.coralRed,
          borderColor: anyDomainFilled ? theme.palette.darkCyan : theme.palette.coralRed,
        }}
      >
        {
          anyDomainFilled ?
          <>
            {
              adminUiDomain.map((item, index) => (
                <Box key={index} sx={{ marginBottom: '10px' }}>
                  {
                    item.domain_name &&
                    <>
                      <Box>{defaultTexts.type}: { item.subdomain_prefix ? 'CNAME' : 'A' }</Box>
                      <Box>{defaultTexts.name}: { item.subdomain_prefix ? item.subdomain_prefix + '.' + item.domain_name : item.domain_name }</Box>
                      <Box>{defaultTexts.value}: { item.subdomain_prefix ? constants.adminUiCNameRecord + '.' : constants.adminUiARecord }</Box>
                      <Divider />
                    </>
                  }                          
                </Box>
              ))
            }
          </> :
          <>{defaultTexts.domainVerifyHelperText}</>
        }
      </Box>
    )
  }
  
  return (
    <>
      <Box sx={{ ...fieldBoxStyle, paddingBottom: '0px' }}>
        <Stack
          direction='row'
          alignItems='center'
          justifyContent='flex-start'
        >
          <Box sx={{ width: '60px' }}>
            <IconButton sx={helpIconStyle} onClick={() => handleHelperDialogOpen(defaultTexts.userInterfaceAdminDomainHelperText)}>
              <HelpOutlineIcon fontSize='large' />
            </IconButton>
          </Box>
          <Box sx={sectionLabelStyle}>{defaultTexts.adminUiDomain}</Box>
        </Stack>
      </Box>

      <Box sx={{ marginBottom: '20px' }}>
        <Grid container>
          <Grid size={{ xs: 12, lg: 9 }}>

            <Box>
              <Grid container>
                <Grid size={{ xs: 12, sm: 9 }}>
                  {
                    adminUiDomain.map((item, index) => (
                      <Box key={index} sx={fieldBoxStyle}>
                        <Stack
                          direction='row'
                          alignItems='center'
                          justifyContent='flex-start'
                        >
                          <Tooltip
                            title={ item.status ? capitalizeFirstLetter(item.status) : defaultTexts.notVerified }
                          >
                            <IconButton>
                              {
                                item.status === 'verified' ?
                                <IoMdCheckmarkCircleOutline size={32} color={theme.palette.mayGreen} /> :
                                <>
                                  {
                                    !item.status ?
                                    <TiWarningOutline size={32} color={theme.palette.deepLemon} /> :
                                    <TiWarningOutline size={32} color={theme.palette.coralRed} />
                                  }
                                </>
                              }                          
                            </IconButton>
                          </Tooltip>
                          <IconButton>
                            {
                              (item.domain_name && isDomainWithCertificate(item.subdomain_prefix, item.domain_name)) ?
                              <TbCertificate size={32} color={theme.palette.mayGreen} /> :
                              <TbCertificateOff size={32} color={theme.palette.deepLemon} />
                            }
                          </IconButton>
                          <CustomTextField 
                            variant='outlined'
                            label={defaultTexts.subdomainPrefixOptional}
                            currentValue={item.subdomain_prefix}
                            setCurrentValue={handleChangeDomain}
                            field_key='subdomain_prefix'
                            index={index}
                            size='small'
                            sx={formControlStyle}
                            outlinedInputSx={outlinedInputStyle}
                          />
                          <Box sx={domainPeriodStyle}>.</Box>
                          <CustomTextField 
                            variant='outlined'
                            label={defaultTexts.domainName}
                            currentValue={item.domain_name}
                            setCurrentValue={handleChangeDomain}
                            required={true}
                            error={getError('admin-domain_name-' + index)}
                            field_key='domain_name'
                            index={index}
                            size='small'
                            sx={formControlStyle}
                            outlinedInputSx={outlinedInputStyle}
                          />
                          {
                            index === 0 ?
                            <IconButton sx={{ color: theme.palette.mayGreen }} onClick={handleAddDomain}>
                              <AddCircleIcon fontSize='large' />
                            </IconButton> :
                            <IconButton sx={{ color: theme.palette.coralRed }} onClick={() => handleRemoveDomain(index)}>
                              <DeleteIcon fontSize='large' />
                            </IconButton>
                          }
                        </Stack>
                      </Box>
                    ))
                  }

                  { width < constants.lgBreakpoint && getDomainVerifyHelper() }
                </Grid>
                <Grid size={{ xs: 12, sm: 3 }}>
                  <Box sx={{ margin: width >= constants.smBreakpoint ? '17px 0px 0px 20px' : '10px 0px 0px 0px' }}>
                    <Button
                      variant='text'
                      disableRipple
                      disableElevation
                      onClick={() => verifyDomain({ type: 'admin', showLoading: true })}
                      sx={verifyButtonStyle}
                    >
                      {defaultTexts.verify}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Box>

            <Box>
              <Grid container>
                <Grid size={{ xs: 12, lg: 9 }}>
                  <Box sx={{ ...fieldBoxStyle, paddingBottom: '0px' }}>
                    <Stack
                      direction='row'
                      alignItems='center'
                      justifyContent='space-between'
                    >
                      <Box>
                        <Stack
                          direction='row'
                          alignItems='center'
                          justifyContent='flex-start'
                        >
                          <Box sx={{ width: '60px' }}>
                            <IconButton sx={helpIconStyle} onClick={() => handleHelperDialogOpen(defaultTexts.rsaCertificateForDomainHelperText)}>
                              <HelpOutlineIcon fontSize='large' />
                            </IconButton>
                          </Box>
                          <Box sx={sectionLabelStyle}>{defaultTexts.rsaCertificateForAdminUiDomain}</Box>
                        </Stack>
                      </Box>
                      <Box>
                        <Stack
                          direction='row'
                          alignItems='center'
                          justifyContent='flex-end'
                        >
                          <Box
                            sx={{
                              ...rsaSwitchStyle,
                              borderRadius: '5px 0px 0px 5px',
                              marginRight: '1px',
                              background: certificateAction === 'upload' ? theme.palette.violetsAreBlue : theme.palette.grayX11,
                            }}
                            onClick={() => handleChangeCertificateAction('upload')}
                          >
                            <Box>{defaultTexts.upload}</Box>
                          </Box>
                          <Box
                            sx={{
                              ...rsaSwitchStyle,
                              borderRadius: '0px 5px 5px 0px',
                              background: certificateAction === 'generate' ? theme.palette.violetsAreBlue : theme.palette.grayX11,
                            }}
                            onClick={() => handleChangeCertificateAction('generate')}
                          >
                            <Box>{defaultTexts.generate}</Box>
                          </Box>
                        </Stack>
                      </Box>
                    </Stack>                
                  </Box>
                </Grid>
                <Grid size={{ lg: 3 }}>
                  {
                    certificateAction === 'upload' &&
                    <Box sx={{ margin: '10px 0px 0px 5px' }}>
                      <Stack
                        direction='row'
                        alignItems='center'
                        justifyContent='flex-start'
                      >
                        <IconButton sx={{ color: theme.palette.mayGreen }} onClick={handleAddCertificate}>
                          <AddCircleIcon fontSize='large' />
                        </IconButton>
                        {
                          adminUiCertificates.length > 1 &&
                          <IconButton sx={{ color: theme.palette.coralRed }} onClick={handleRemoveCertificate}>
                            <RemoveCircleIcon fontSize='large' />
                          </IconButton>
                        }
                      </Stack>
                    </Box> 
                  }
                </Grid>
              </Grid>
            </Box>

            <Box>
              <Grid container>
                <Grid size={{ lg: 9 }}>
                  {
                    certificateAction === 'upload' && adminUiCertificates.map((item, index) => (
                      <Box key={index}>

                        <Box sx={fieldBoxStyle}>
                          <Stack
                            direction='row'
                            alignItems='center'
                            justifyContent='flex-start'
                          >
                            <Box sx={{ width: '60px' }}></Box>              
                            <CustomTextField 
                              variant='outlined'
                              label={defaultTexts.copyYourCertificateHere + '...'}
                              currentValue={item.certificate}
                              setCurrentValue={handleChangeCertificates}
                              required={true}
                              error={getError('certificate-' + index)}
                              field_key='certificate'
                              index={index}
                              size='small'
                              multiline={true}
                              sx={{ ...formControlStyle, width: '100%' }}
                              outlinedInputSx={outlinedInputStyle}
                            />
                          </Stack>
                        </Box>

                        <Box sx={fieldBoxStyle}>
                          <Stack
                            direction='row'
                            alignItems='center'
                            justifyContent='flex-start'
                          >
                            <Box sx={{ width: '60px' }}></Box>              
                            <CustomTextField 
                              variant='outlined'
                              label={defaultTexts.copyYourPrivateKeyHere + '...'}
                              currentValue={item.private_key}
                              setCurrentValue={handleChangeCertificates}
                              required={true}
                              error={getError('private_key-' + index)}
                              field_key='private_key'
                              index={index}
                              size='small'
                              multiline={true}
                              sx={{ ...formControlStyle, width: '100%' }}
                              outlinedInputSx={outlinedInputStyle}
                            />
                          </Stack>
                        </Box>

                        <Box sx={fieldBoxStyle}>
                          <Stack
                            direction='row'
                            alignItems='center'
                            justifyContent='flex-start'
                          >
                            <Box sx={{ width: '60px' }}></Box>              
                            <CustomTextField 
                              variant='outlined'
                              label={defaultTexts.copyYourFullChainHere + '...'}
                              currentValue={item.full_chain}
                              setCurrentValue={handleChangeCertificates}
                              error={getError('full_chain-' + index)}
                              field_key='full_chain'
                              index={index}
                              size='small'
                              multiline={true}
                              sx={{ ...formControlStyle, width: '100%' }}
                              outlinedInputSx={outlinedInputStyle}
                            />
                          </Stack>
                        </Box>

                        {
                          index !== adminUiCertificates.length - 1 &&
                          <Box sx={{ ...fieldBoxStyle, marginLeft: '60px' }}>
                            <Divider />
                          </Box>
                        }

                      </Box>
                    ))
                  }
                </Grid>
              </Grid>
            </Box>

          </Grid>
          <Grid size={{ xs: 12, lg: 3 }}>
            { width >= constants.lgBreakpoint && getDomainVerifyHelper() }
          </Grid>
        </Grid>
      </Box>

      {/* --------------------------------------------------------------------------------- */}
      {/* --------------------------------------------------------------------------------- */}
      {/* --------------------------------------------------------------------------------- */}
      {/* --------------------------------------------------------------------------------- */}
      {/* --------------------------------------------------------------------------------- */}

      <Grid container spacing={{ xs: 0, lg: 2, xl: 0 }}>
        <Grid size={{ xs: 12, lg: 6, xl: 5 }}>

          <Box sx={{ ...fieldBoxStyle, marginTop: '20px' }}>
            <Stack
              direction='row'
              alignItems='center'
              justifyContent='flex-start'
            >
              <Box sx={{ width: '60px' }}>
                <IconButton sx={helpIconStyle} onClick={() => handleHelperDialogOpen(defaultTexts.googleRecaptchaConfigurationHelperText)}>
                  <HelpOutlineIcon fontSize='large' />
                </IconButton>
              </Box>              
              <Box sx={sectionLabelStyle}>{defaultTexts.googleRecaptchaConfiguration}</Box>
            </Stack>
          </Box>

          <Box sx={fieldBoxStyle}>
            <Stack
              direction='row'
              alignItems='center'
              justifyContent='flex-start'
            >
              <Box sx={{ width: '60px' }}></Box>              
              <CustomTextField 
                variant='outlined'
                label={defaultTexts.siteKey}
                currentValue={adminUiSiteKey}
                setCurrentValue={handleChangeSiteKey}
                required={true}
                error={getError('adminUiSiteKey')}
                size='small'
                sx={formControlStyle}
                outlinedInputSx={outlinedInputStyle}
              />
            </Stack>
          </Box>

          <Box sx={fieldBoxStyle}>
            <Stack
              direction='row'
              alignItems='center'
              justifyContent='flex-start'
            >
              <Box sx={{ width: '60px' }}></Box>              
              <CustomTextField 
                variant='outlined'
                label={defaultTexts.secretKey}
                currentValue={adminUiSecretKey}
                setCurrentValue={handleChangeSecretKey}
                required={true}
                error={getError('adminUiSecretKey')}
                size='small'
                sx={formControlStyle}
                outlinedInputSx={outlinedInputStyle}
              />
            </Stack>
          </Box>

        </Grid>
        <Grid size={{ xs: 12, lg: 6, xl: 5 }} offset={{ lg: 0, xl: 2 }}>

          <Box sx={appearanceLabelStyle}>{defaultTexts.customizeYourInterfaceAppearance}</Box>

          {
            (adminUiLogo && typeof adminUiLogo === 'string') &&
            <Stack
              direction='row'
              alignItems='center'
              justifyContent='flex-start'
              spacing={2}
              sx={{
                borderBottom: `${theme.palette.lightSilver} 1px dotted`,
                paddingBottom: '10px',
                marginBottom: '20px',
              }}
            >
              <Box>
                <img src={constants.s3URL + constants.s3Bucket + adminUiLogo} alt='' style={{ maxWidth: '200px' }} />
              </Box>
              <Box sx={valueMdStyle}>{defaultTexts.adminUiLogo}</Box>
            </Stack>
          }

          <Stack
            direction='row'
            alignItems='center'
            justifyContent='center'
            spacing={2}
            sx={{
              borderBottom: `${theme.palette.lightSilver} 1px solid`,
              paddingBottom: '20px',
              marginBottom: '20px',
            }}
          >
            <Button 
              component='label'
              role={undefined}
              variant='contained'
              tabIndex={-1}
              sx={uploadButtonStyle}
            >
            {defaultTexts.logo}
              <VisuallyHiddenInput type='file' onChange={handleLogoUpload} />
            </Button>
            <Box sx={uploadLogoDescriptionStyle}>{ adminUiLogo ? adminUiLogo.name : defaultTexts.uploadLogosFile}</Box>
          </Stack>

          <Stack
            direction='row'
            alignItems='center'
            justifyContent='center'
            spacing={2}
            sx={{ marginBottom: '20px' }}
          >
            <Box
              sx={{ ...pickColorStyle, background: adminUiPrimaryColor }}
              onClick={handlePrimaryColorDialogOpen}
            ></Box>
            <Box sx={uploadLogoDescriptionStyle}>{defaultTexts.primaryColor}</Box>
          </Stack>

          <Stack
            direction='row'
            alignItems='center'
            justifyContent='center'
            spacing={2}
            sx={{ marginBottom: '20px' }}
          >
            <Box
              sx={{ ...pickColorStyle, background: adminUiSecondaryColor }}
              onClick={handleSecondaryColorDialogOpen}
            ></Box>
            <Box sx={uploadLogoDescriptionStyle}>{defaultTexts.secondaryColor}</Box>
          </Stack>

        </Grid>
      </Grid>



      <CustomDialog
        open={helperDialogOpen}
        onClose={handleHelperDialogClose}
        dialogBody={{
          text: helperText,
        }}
      />

      <CustomDialog
        open={primaryColorDialogOpen}
        onClose={handlePrimaryColorDialogClose}
        dialogTitle={{ title: defaultTexts.primaryColor }}
        actionButtons={[{ title: defaultTexts.apply, onClick: handlePrimaryColorDialogClose }]}
        maxWidth='xs'
      >
        <CustomColorPicker
          color={adminUiPrimaryColor}
          setColor={setAdminUiPrimaryColor}
        />
      </CustomDialog>

      <CustomDialog
        open={secondaryColorDialogOpen}
        onClose={handleSecondaryColorDialogClose}
        dialogTitle={{ title: defaultTexts.secondaryColor }}
        actionButtons={[{ title: defaultTexts.apply, onClick: handleSecondaryColorDialogClose }]}
        maxWidth='xs'
      >
        <CustomColorPicker
          color={adminUiSecondaryColor}
          setColor={setAdminUiSecondaryColor}
        />
      </CustomDialog>
    </>
  )
}

export default AdminUiEdit